import { defineStore } from 'pinia'
import { pick, isEqual } from 'lodash-es'
import { reactive, shallowRef } from '@vue/composition-api'
import { getStorage } from '@ms/utils/storage'
import { getAdInfo } from 'wap/common/api'
import { useLayoutStore } from 'wap/stores/layout'
import { setDownloadInfo, getDownloadSpotId } from './useDownloadApp'
import useFeaturedMatches from './useFeaturedMatches.js'
import useHomeLive from './useHomeLive.js'
import useHighlights from './useHighlights.js'
import useToday from './useToday.js'
import useNext7Days from './useNext7Days.js'
import useTopLeagues from './useTopLeagues.js'

const KEY_HOME_BANNERS = 'home-banners'
const KEY_SECOND_NAV_DISPLAYS = 'second-nav-displays'
const KEY_SECOND_NAV_GAMES = 'second-nav-games'
const KEY_SECOND_NAV_OTHERS = 'second-nav-others'
const secondNavCacheKeys = ['href', 'image', 'text', 'flagText']

const useHomeStore = defineStore('home', () => {
  const layoutStore = useLayoutStore()
  const secondNavConfig = reactive({
    displays: {
      ads: getStorage(KEY_SECOND_NAV_DISPLAYS, []),
      spotTitle: 'Popular'
    },
    games: {
      ads: getStorage(KEY_SECOND_NAV_GAMES, []),
      spotTitle: 'Games'
    },
    others: {
      ads: getStorage(KEY_SECOND_NAV_OTHERS, []),
      spotTitle: 'Sports'
    }
  })

  const bannerList = shallowRef(getStorage(KEY_HOME_BANNERS))
  const bubbleAd = shallowRef(null)
  const popupAd = shallowRef(null)

  const updateSecondNavConfig = (type, adList, x, cacheKey) => {
    if (adList.length) {
      const list = adList.map(x => pick(x, secondNavCacheKeys))
      if (!isEqual(list, secondNavConfig[type].ads)) {
        secondNavConfig[type].ads = list
        secondNavConfig[type].spotTitle = x.spotTitle
        localStorage.setItem(cacheKey, JSON.stringify(list))
      }
    }
  }

  // 获取首页广告位相关配置
  const getHomeConfig = () => {
    const adIds = [
      'HomeFirstclassNavicon',
      'HomeTopBanner',
      'HomeNewNaviconDisplay',
      'HomeNewNaviconGames',
      'HomeNewNaviconOthers'
    ]
    const downloadSpotId = getDownloadSpotId()
    if (downloadSpotId) {
      adIds.unshift(downloadSpotId)
    }
    getAdInfo(adIds).then(data => {
      let hasFirstNav = false
      data && data.forEach(x => {
        const adList = x.ads
        switch (x.spotId) {
          case 'WapHomeDownloadApp':
            setDownloadInfo(x.ads)
            break
          case 'HomeFirstclassNavicon':
            layoutStore.setFirstNavs(x.ads)
            hasFirstNav = true
            break
          case 'HomeNewNaviconDisplay':
            updateSecondNavConfig('displays', adList, x, KEY_SECOND_NAV_DISPLAYS)
            break
          case 'HomeNewNaviconGames':
            updateSecondNavConfig('games', adList, x, KEY_SECOND_NAV_GAMES)
            break
          case 'HomeNewNaviconOthers':
            updateSecondNavConfig('others', adList, x, KEY_SECOND_NAV_OTHERS)
            break
          case 'HomeTopBanner': {
            const list = adList.map(x => pick(x, ['href', 'image', 'name']))
            if (!isEqual(list, bannerList.value)) {
              bannerList.value = list
              localStorage.setItem(KEY_HOME_BANNERS, JSON.stringify(list))
            }
          }
            break
          default:
              // no default
        }
      })

      if (!hasFirstNav) {
        // set default
        layoutStore.setFirstNavs()
      }
    }).catch(error => {
      // set default
      layoutStore.setFirstNavs()
      console.error(error)
    })
  }

  return {
    secondNavConfig,
    bannerList,
    bubbleAd,
    popupAd,
    getHomeConfig,
    ...useFeaturedMatches(),
    ...useHomeLive(),
    ...useHighlights(),
    ...useToday(),
    ...useNext7Days(),
    ...useTopLeagues()
  }
})

export default useHomeStore
